import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { useFeatureFlags } from './contexts/FeatureFlagContext';
import AuthPage from './components/AuthPage';
import HomePage from './components/HomePage';

import MyMatchesPage from './components/MyMatchesPage';
import ProfilePage from './components/ProfilePage';
import ReviewsPage from './components/ReviewsPage';
import CreateMatch from './components/CreateMatch';
import PlayHubPage from './components/PlayHubPage';
import DiscoverMatchesPage from './components/DiscoverMatchesPage';
import MatchPreviewPage from './components/MatchPreviewPage';
import MyJoinApplicationsPage from './components/MyJoinApplicationsPage';
import JoinMatch from './components/JoinMatch';
import LeaveMatch from './components/LeaveMatch';
import ViewMatch from './components/ViewMatch';
import RatePostMatchPage from './components/RatePostMatchPage';
import SettingsPage from './components/SettingsPage';
import FriendsPage from './components/FriendsPage';
import GroupsPage from './components/GroupsPage';
import GroupDetailPage from './components/GroupDetailPage';
import GroupJoinPage from './components/GroupJoinPage';
import CreateGroupPage from './components/CreateGroupPage';
import { HeadToHeadPage } from './components/h2h/HeadToHeadPage';
import PublicProfilePage from './components/PublicProfilePage';
import ProtectedRoute from './components/ProtectedRoute';
import OwnerRoute from './components/OwnerRoute';
import VenueRoute from './components/VenueRoute';
import AdminRoute from './components/AdminRoute';
import AuthGate from './components/AuthGate';
import { AuthCallback, AuthFailure } from './components/AuthCallbacks';
import Sidebar from "./components/Sidebar";
import { WelcomeModal } from "./components/WelcomeModal";
import { useResponsive } from './hooks/useResponsive';
import LandingPage from './components/LandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import FAQPage from './components/FAQPage';
import ContactPage from './components/ContactPage';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './contexts/AuthContext';
import { useSilentUpdate } from './hooks/useSilentUpdate';
import RoleSelectionModal from './components/modals/RoleSelectionModal';
import * as analytics from './lib/analytics';
// import { FeedbackButton } from './components/FeedbackButton'; // Removed - UX improvement

// Venue Pages
import VenueSearchPage from './pages/venues/VenueSearchPage';
import VenueDetailPage from './pages/venues/VenueDetailPage';
import MyBookingsPage from './pages/venues/MyBookingsPage';
import BookingDetailPage from './pages/venues/BookingDetailPage';
import PaymentPage from './pages/venues/PaymentPage';
import OwnerDashboardPage from './pages/venues/owner/OwnerDashboardPage';
import CreateVenuePage from './pages/venues/owner/CreateVenuePage';
import AddFieldPage from './pages/venues/owner/AddFieldPage';
import CreateComplexPage from './pages/venues/owner/CreateComplexPage';
import ComplexDetailPage from './pages/venues/owner/ComplexDetailPage';
import ComplexSetupPage from './pages/venues/owner/ComplexSetupPage';
import ManageBookingsPage from './pages/venues/owner/ManageBookingsPage';
import ManageVenuesPage from './pages/venues/owner/ManageVenuesPage';
import VenueAvailabilityPage from './pages/venues/owner/VenueAvailabilityPage';
import BookingSuccessPage from './pages/BookingSuccessPage';
import BookingFailurePage from './pages/BookingFailurePage';

// New Enhanced Venue Pages
import OwnerAnalyticsPage from './pages/venues/owner/OwnerAnalyticsPage';
import ComplexDetailPageEnhanced from './pages/venues/owner/ComplexDetailPageEnhanced';
import ManageVenuesPageEnhanced from './pages/venues/owner/ManageVenuesPageEnhanced';

// New Owner Management Pages (ATC Style)
import ReservationGridPage from './pages/venues/owner/ReservationGridPage';
import FixedBookingsPage from './pages/venues/owner/FixedBookingsPage';
import ReportsPage from './pages/venues/owner/ReportsPage';
import OwnerSettingsPage from './pages/venues/owner/OwnerSettingsPage';

// Owner Onboarding & Landing
import OwnerLandingPage from './pages/venues/owner/OwnerLandingPage';
import OnboardingWizard from './pages/venues/owner/OnboardingWizard';
import DemoPage from './pages/venues/DemoPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminClubsPage from './pages/admin/AdminClubsPage';
import AdminCreateClubPage from './pages/admin/AdminCreateClubPage';
import AdminClubDetailPage from './pages/admin/AdminClubDetailPage';

// Public Pages
import PublicClubPage from './pages/PublicClubPage';
import { ReplacementClaimPage } from './pages/ReplacementClaimPage';
import CounterPage from './pages/counter/CounterPage';
import RewardsRoute from './components/RewardsRoute';
import OwnerRewardsPage from './pages/venues/owner/OwnerRewardsPage';
import RewardsPage from './pages/rewards/RewardsPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PublicMatchRecapPage from './pages/PublicMatchRecapPage';

// Wrapper component for ViewMatch
const ViewMatchWrapper: React.FC = () => {
  return <ViewMatch />;
};

// Guard component to require complex before accessing certain owner pages
const RequireComplexRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const [hasComplex, setHasComplex] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkComplex = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setHasComplex(false);
          setLoading(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/complexes/complex`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setHasComplex(true);
        } else {
          setHasComplex(false);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking complex:', error);
        // On network error, assume complex exists to avoid redirect loop
        setHasComplex(true);
      } finally {
        setLoading(false);
      }
    };

    checkComplex();
  }, []);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Cargando...</div>;
  }

  if (!hasComplex) {
    return <Navigate to="/owner/onboarding" replace />;
  }

  return children;
};

// Root route component: show landing for guests, redirect based on role if logged in
const RootRoute: React.FC = () => {
  const { user, loading, checkNeedsRoleSelection } = useAuth();
  const { venues: venuesEnabled } = useFeatureFlags();

  // Mientras está cargando, mostrar un loading spinner
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#181A1B'
      }}>
        <div style={{
          color: '#00E676',
          fontSize: '24px',
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif"
        }}>
          Cargando...
        </div>
      </div>
    );
  }

  // Si el usuario está logueado
  if (user) {
    // eslint-disable-next-line no-console
    console.log('🔍 RootRoute - Usuario detectado:', { email: user.email, role: user.role });

    // Si necesita seleccionar rol, redirigir a home (el modal se mostrará ahí)
    if (checkNeedsRoleSelection()) {
      // eslint-disable-next-line no-console
      console.log('⚠️ Usuario necesita seleccionar rol');
      return <Navigate to="/home" replace />;
    }

    try {
      const pendingMatchJoin = localStorage.getItem('pendingMatchJoin');
      if (pendingMatchJoin) {
        localStorage.removeItem('pendingMatchJoin');
        return <Navigate to={`/match/${pendingMatchJoin}/preview`} replace />;
      }
    } catch {
      localStorage.removeItem('pendingMatchJoin');
    }

    try {
      const landingFormData = localStorage.getItem('landingFormData');

      if (landingFormData) {
        // Si hay datos del formulario, redirigir a create-match
        // eslint-disable-next-line no-console
        console.log('📝 Landing form data detectado, redirigiendo a create-match');
        return <Navigate to="/create-match" replace />;
      }
    } catch {
      // Si hay error, limpiar localStorage y continuar con redirección normal
      localStorage.removeItem('landingFormData');
    }

    // Redirigir según rol
    if (user.role === 'VENUE_OWNER' && venuesEnabled) {
      // eslint-disable-next-line no-console
      console.log('🏟️ Usuario es VENUE_OWNER, redirigiendo a /owner/reservations');
      return <Navigate to="/owner/reservations" replace />;
    }

    // Por defecto (PLAYER o BOTH), ir a home
    // eslint-disable-next-line no-console
    console.log('⚽ Usuario es PLAYER o BOTH, redirigiendo a /home');
    return <Navigate to="/home" replace />;
  }

  // Si no está logueado, mostrar landing page
  return <LandingPage />;
};

// Main App Component!!!!!!!!
const AppContent: React.FC = () => {
  const { user } = useAuth();
  return (
    <>
      {user && <WelcomeModal />}
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/home" element={
        <ProtectedRoute>
          <HomePage />
        </ProtectedRoute>
      } />
      <Route path="/create-match" element={
        <ProtectedRoute>
          <CreateMatch />
        </ProtectedRoute>
      } />
      <Route path="/play" element={
        <ProtectedRoute>
          <PlayHubPage />
        </ProtectedRoute>
      } />
      <Route path="/discover" element={
        <ProtectedRoute>
          <DiscoverMatchesPage />
        </ProtectedRoute>
      } />
      <Route path="/my-applications" element={
        <ProtectedRoute>
          <MyJoinApplicationsPage />
        </ProtectedRoute>
      } />

      <Route path="/matches" element={
        <ProtectedRoute>
          <MyMatchesPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <AuthGate>
          <SettingsPage />
        </AuthGate>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/friends" element={
        <ProtectedRoute>
          <FriendsPage />
        </ProtectedRoute>
      } />
      <Route path="/groups" element={
        <ProtectedRoute>
          <GroupsPage />
        </ProtectedRoute>
      } />
      <Route path="/groups/create" element={
        <ProtectedRoute>
          <CreateGroupPage />
        </ProtectedRoute>
      } />
      <Route path="/groups/:id" element={
        <ProtectedRoute>
          <GroupDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/groups/:groupId/h2h" element={
        <ProtectedRoute>
          <HeadToHeadPage />
        </ProtectedRoute>
      } />
      <Route path="/public-profile/:id" element={<PublicProfilePage />} />
      <Route path="/reviews" element={
        <ProtectedRoute>
          <ReviewsPage />
        </ProtectedRoute>
      } />
      <Route path="/join-group/:token" element={<GroupJoinPage />} />
      <Route path="/join/:id" element={<JoinMatch />} />
      <Route path="/reemplazo/:token" element={<ReplacementClaimPage />} />
      {/* Mostrador: no lleva login ni feature flag. Si el link es inválido, la
          propia pantalla lo dice. Meterla detrás del flag dejaría al encargado
          mirando una redirección sin explicación. */}
      <Route path="/mostrador" element={<CounterPage />} />
      <Route path="/match/:id" element={
        <AuthGate>
          <ViewMatchWrapper />
        </AuthGate>
      } />
      <Route path="/match/:id/rate" element={
        <AuthGate>
          <RatePostMatchPage />
        </AuthGate>
      } />
      <Route path="/match/:id/preview" element={<MatchPreviewPage />} />
      <Route path="/match/:id/recap" element={<PublicMatchRecapPage />} />
      <Route path="/leave/:playerId/:token" element={<LeaveMatch />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/failure" element={<AuthFailure />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contacto" element={<ContactPage />} />

      {/* Owner Landing - Public (B2B) */}
      <Route path="/canchas" element={<VenueRoute><OwnerLandingPage /></VenueRoute>} />
      {/* Panel de premios. Detrás de su propio flag: el panel de reservas
          (VenueRoute) sigue dormido y son dos cosas distintas. */}
      <Route path="/canchas/premios" element={
        <RewardsRoute><ProtectedRoute><OwnerRewardsPage /></ProtectedRoute></RewardsRoute>
      } />
      {/* Puntos y premios del jugador. */}
      <Route path="/premios" element={
        <RewardsRoute><ProtectedRoute><RewardsPage /></ProtectedRoute></RewardsRoute>
      } />

      {/* Public Demo — auto-login as demo owner */}
      <Route path="/demo" element={<DemoPage />} />

      {/* Venue Routes - Public */}
      <Route path="/venues" element={<VenueRoute><VenueSearchPage /></VenueRoute>} />
      <Route path="/venues/:id" element={<VenueRoute><VenueDetailPage /></VenueRoute>} />
      <Route path="/reservar/:complexSlug/:venueSlug" element={<VenueRoute><VenueDetailPage /></VenueRoute>} />

      {/* Venue Routes - User */}
      <Route path="/my-bookings" element={
        <VenueRoute>
          <ProtectedRoute>
            <MyBookingsPage />
          </ProtectedRoute>
        </VenueRoute>
      } />
      <Route path="/bookings/:id" element={
        <VenueRoute>
          <ProtectedRoute>
            <BookingDetailPage />
          </ProtectedRoute>
        </VenueRoute>
      } />
      <Route path="/bookings/payment" element={
        <VenueRoute>
          <ProtectedRoute>
            <PaymentPage />
          </ProtectedRoute>
        </VenueRoute>
      } />

      {/* Venue Routes - Owner */}
      <Route path="/owner/onboarding" element={
        <VenueRoute>
          <OwnerRoute>
            <OnboardingWizard />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/dashboard" element={
        <VenueRoute>
          <OwnerRoute>
            <Navigate to="/owner/reservations" replace />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/analytics" element={
        <VenueRoute>
          <OwnerRoute>
            <OwnerAnalyticsPage />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/venues" element={
        <VenueRoute>
          <OwnerRoute>
            <ManageVenuesPage />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/venues/enhanced" element={
        <VenueRoute>
          <OwnerRoute>
            <ManageVenuesPageEnhanced />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/setup" element={
        <VenueRoute>
          <OwnerRoute>
            <ComplexSetupPage />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/complex/create" element={
        <VenueRoute>
          <OwnerRoute>
            <CreateComplexPage />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/venues/create" element={
        <VenueRoute>
          <OwnerRoute>
            <RequireComplexRoute>
              <CreateVenuePage />
            </RequireComplexRoute>
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/venues/:id/edit" element={
        <VenueRoute>
          <OwnerRoute>
            <RequireComplexRoute>
              <CreateVenuePage />
            </RequireComplexRoute>
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/venues/:id/availability" element={
        <VenueRoute>
          <OwnerRoute>
            <RequireComplexRoute>
              <VenueAvailabilityPage />
            </RequireComplexRoute>
          </OwnerRoute>
        </VenueRoute>
      } />
      {/* REMOVED: /owner/complexes - Owners now have only ONE complex */}
      <Route path="/owner/complex" element={
        <VenueRoute>
          <OwnerRoute>
            <ComplexDetailPage />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/complex/enhanced" element={
        <VenueRoute>
          <OwnerRoute>
            <ComplexDetailPageEnhanced />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/complex/edit" element={
        <VenueRoute>
          <OwnerRoute>
            <CreateComplexPage />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/complex/add-field" element={
        <VenueRoute>
          <OwnerRoute>
            <AddFieldPage />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/bookings" element={
        <VenueRoute>
          <OwnerRoute>
            <ManageBookingsPage />
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/reservations" element={
        <VenueRoute>
          <OwnerRoute>
            <RequireComplexRoute>
              <ReservationGridPage />
            </RequireComplexRoute>
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/fixed-bookings" element={
        <VenueRoute>
          <OwnerRoute>
            <RequireComplexRoute>
              <FixedBookingsPage />
            </RequireComplexRoute>
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/reports" element={
        <VenueRoute>
          <OwnerRoute>
            <RequireComplexRoute>
              <ReportsPage />
            </RequireComplexRoute>
          </OwnerRoute>
        </VenueRoute>
      } />
      <Route path="/owner/settings" element={
        <VenueRoute>
          <OwnerRoute>
            <OwnerSettingsPage />
          </OwnerRoute>
        </VenueRoute>
      } />

      {/* Mercado Pago Payment Routes - Public (callback desde MP) */}
      <Route path="/bookings/success" element={<VenueRoute><BookingSuccessPage /></VenueRoute>} />
      <Route path="/bookings/failure" element={<VenueRoute><BookingFailurePage /></VenueRoute>} />
      <Route path="/bookings/pending" element={<VenueRoute><BookingSuccessPage /></VenueRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      <Route path="/admin/clubs" element={
        <AdminRoute>
          <AdminClubsPage />
        </AdminRoute>
      } />
      <Route path="/admin/clubs/create" element={
        <AdminRoute>
          <AdminCreateClubPage />
        </AdminRoute>
      } />
      <Route path="/admin/clubs/:id" element={
        <AdminRoute>
          <AdminClubDetailPage />
        </AdminRoute>
      } />

      {/* Public Club Page */}
      <Route path="/club/:slug" element={<PublicClubPage />} />
    </Routes>
    </>
  );
};

const App: React.FC = () => {
  const { isMobile } = useResponsive();
  const { user, selectRole, checkNeedsRoleSelection } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Pages that render full-screen without the player sidebar
  const noSidebarPaths = ['/canchas', '/demo'];
  const hideSidebar = noSidebarPaths.some(p => location.pathname.startsWith(p));

  // Demo mode detection
  const isDemoUser = user?.email === 'demo@futbolero.com';

  const [showRoleModal, setShowRoleModal] = useState(false);

  // Ejecutar actualizaciones silenciosas en segundo plano
  useSilentUpdate();

  // Initialize analytics on mount
  useEffect(() => {
    analytics.init();
  }, []);

  // Track pageviews on route changes
  useEffect(() => {
    analytics.track('$pageview', { '$current_url': window.location.href });
  }, [location.pathname]);

  // Capturar ?ref=CODE en cualquier ruta de entrada (no solo /auth). Así el
  // referido sobrevive aunque el usuario llegue por un link de partido o grupo
  // (/join/:id?ref=, /join-group/:token?ref=) y recién después se registre.
  // AuthPage lo levanta desde localStorage al momento del signup.
  useEffect(() => {
    try {
      const urlCode = new URLSearchParams(window.location.search).get('ref')?.trim();
      if (urlCode) {
        localStorage.setItem('pendingReferralCode', urlCode);
      }
    } catch { /* ignore */ }
  }, []);

  // NO agregar acá un efecto de identify/reset.
  //
  // Había uno que hacía `if (user?.id) identify() else reset()`. Como `user`
  // arranca en null mientras AuthContext valida el token contra /api/auth/me,
  // el reset() corría en CADA page load — también para usuarios logueados — y
  // posthog.reset() rota el distinct_id anónimo. Resultado: cada visita contaba
  // como una persona nueva y el funnel anónimo→registrado quedaba partido entre
  // dos IDs distintos.
  //
  // La identidad la maneja AuthContext, que es el único que sabe cuándo la
  // sesión está realmente resuelta: identify() al resolver /api/auth/me y
  // reset() sólo en el logout explícito.
  // Ver docs/superpowers/specs/2026-09-21-analytics-instrumentation-design.md §4.1

  // Mostrar modal de selección de rol si es necesario
  useEffect(() => {
    if (checkNeedsRoleSelection()) {
      setShowRoleModal(true);
    }
  }, [user, checkNeedsRoleSelection]);

  // Handler para seleccionar rol
  const handleRoleSelected = async (role: 'PLAYER' | 'VENUE_OWNER') => {
    try {
      await selectRole(role);
      setShowRoleModal(false);

      // Redirigir según rol seleccionado
      if (role === 'VENUE_OWNER') {
        navigate('/owner/reservations');
      } else {
        navigate('/home');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error selecting role:', error);
      alert('Error al seleccionar el rol. Por favor, intenta nuevamente.');
    }
  };

  // Force Vercel redeploy - WhatsApp Bot integration ready

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#181A1B' }}>
      {user && !hideSidebar && <Sidebar />}
      <div style={{
        flex: 1,
        marginLeft: isMobile || !user || hideSidebar ? 0 : 220,
        marginTop: 0,
        padding: 0,
        minHeight: '100vh',
        width: '100%'
      }}>
        {/* Demo mode banner */}
        {isDemoUser && !hideSidebar && (
          <div style={{
            background: 'linear-gradient(90deg, #00E67615 0%, #1DE9B615 100%)',
            borderBottom: '1px solid #00E67640',
            padding: '10px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}>
            <span style={{ color: '#00E676', fontSize: '13px', fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
              🎯 Modo Demo — Estás viendo el panel de <strong>Complejo El Estadio</strong>
            </span>
            <a
              href="/canchas#solicitar-demo"
              style={{
                background: '#00E676',
                color: '#0A0A0A',
                padding: '6px 16px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 700,
                textDecoration: 'none',
                fontFamily: "'Inter', sans-serif",
                whiteSpace: 'nowrap',
              }}
            >
              Solicitar acceso para mi cancha →
            </a>
          </div>
        )}
        <ErrorBoundary scope="app">
          <AppContent />
        </ErrorBoundary>
      </div>
      {/* {user && <FeedbackButton />} */} {/* Removed - UX improvement */}

      {/* Role Selection Modal */}
      <RoleSelectionModal
        isOpen={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        onRoleSelected={handleRoleSelected}
      />
    </div>
  );
};

export default App; 